import { Link } from 'react-router-dom';
import { BarChart3, Zap } from 'lucide-react';

import type { NostrEvent } from '@nostrify/nostrify';
import { Badge } from '@/components/ui/badge';
import { AuthorLink } from '@/components/stashstr/AuthorLink';
import { VoteArrows } from '@/components/stashstr/VoteArrows';
import { UnprovenImage } from '@/components/stashstr/UnprovenImage';
import type { FeedEntry } from '@/hooks/useStashstrFeed';
import {
  extractDomain,
  getImetas,
  getLink,
  getPollId,
  getTerritories,
  getTitle,
  sanitizeUrl,
  timeAgo,
  toNevent,
} from '@/lib/stashstr';
import { cn } from '@/lib/utils';

interface ItemRowProps {
  entry: FeedEntry;
}

export function ItemRow({ entry }: ItemRowProps) {
  const { event, powValid, commentCount } = entry;

  // Unproven posts render fully expanded (only images are gated) with a badge.
  if (!powValid) {
    return (
      <div className="relative rounded-md border border-amber-400 dark:border-amber-700 bg-[#f6f7f9] dark:bg-[#1e2023] p-3 mb-2">
        <Badge
          variant="outline"
          className="text-[10px] border-amber-400 text-amber-600 dark:text-amber-400 absolute top-2 right-2"
        >
          Unproven
        </Badge>
        <CardInner entry={entry} badgeOffset />
      </div>
    );
  }

  return (
    <div className="rounded-md border border-gray-200 dark:border-[#2b2f34] bg-[#f6f7f9] dark:bg-[#1e2023] p-3 mb-2">
      <CardInner entry={entry} />
    </div>
  );
}

function CardInner({ entry, badgeOffset }: { entry: FeedEntry; badgeOffset?: boolean }) {
  return (
    <div className={cn('flex gap-2', badgeOffset && 'pr-16')}>
      <VoteArrows
        targetId={entry.event.id}
        targetKind="11"
        targetPubkey={entry.event.pubkey}
        disabled={!entry.powValid}
      />
      <RowContent entry={entry} />
    </div>
  );
}

function RowContent({ entry }: { entry: FeedEntry }) {
  const { event, powValid, zapTotal, zapUnverifiedTotal, zapUnverifiedCount, commentCount, author } =
    entry;
  const title = getTitle(event);
  const link = getLink(event);
  const domain = extractDomain(link);
  const territories = getTerritories(event);
  const pollId = getPollId(event);
  const imetas = getImetas(event);
  const thumb = imetas[0];

  return (
    <div className="min-w-0 flex-1">
      <div className="text-[15px] leading-snug">
        <Link
          to={`/items/${toNevent(event)}`}
          className="font-semibold text-[#1a56db] dark:text-[#6ea8fe] hover:underline"
        >
          {title}
        </Link>
        {domain && (
          <span className="text-[12px] text-gray-500 dark:text-gray-400 ml-1">({domain})</span>
        )}
        {pollId && (
          <span title="Poll">
            <BarChart3 className="size-3.5 inline ml-1.5 text-[#f97316] dark:text-[#fb923c]" />
          </span>
        )}
      </div>
      <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-1.5 items-center">
        <span className="inline-flex items-center gap-0.5">
          <Zap className="size-3 text-[#f97316] dark:text-[#fb923c]" fill="currentColor" />
          {zapTotal} sats
          {zapUnverifiedCount > 0 && (
            <span
              className="text-[11px] text-amber-600 dark:text-amber-400"
              title="Zaps from receipts without a recipient tag — not counted toward ranking."
            >
              (+{zapUnverifiedTotal} unverified)
            </span>
          )}
        </span>
        <span aria-hidden>\</span>
        <Link
          to={`/items/${toNevent(event)}`}
          className="hover:text-gray-800 dark:hover:text-gray-200"
        >
          {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
        </Link>
        <span aria-hidden>\</span>
        <AuthorLink pubkey={event.pubkey} name={author?.display_name || author?.name} />
        <span>{timeAgo(event.created_at)}</span>
        {territories.map((t) => (
          <Link
            key={t}
            to={`/t/${t}`}
            className="hover:text-gray-800 dark:hover:text-gray-200"
          >
            ~{t}
          </Link>
        ))}
      </div>
      {thumb &&
        (powValid ? (
          <img
            src={sanitizeUrl(thumb)}
            alt=""
            loading="lazy"
            className="w-16 h-16 object-cover rounded shrink-0 mt-2"
          />
        ) : (
          <UnprovenImage url={thumb} variant="thumb" className="w-16 h-16 shrink-0 mt-2" />
        ))}
    </div>
  );
}
